import { apiRequest } from "./client";

// Lightweight session entry used in the Studio sidebar. Doesn't include
// message bodies — fetched on demand via `chatsApi.get(id)` when the
// parent switches to it.
export type ChatSessionSummary = {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  _count: { messages: number };
};

export type ChatMessageRow = {
  id: string;
  sessionId: string;
  role: "user" | "assistant";
  text: string | null;
  // Persisted images come back as a JSON array of data URLs.
  images: string[];
  generationId: string | null;
  createdAt: string;
};

export type ChatSession = {
  id: string;
  parentId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: ChatMessageRow[];
};

// All endpoints are JWT-gated and scoped to the calling parent. 404 on
// cross-family access (existence stays private). See the backend
// ChatsService for that policy.
export const chatsApi = {
  list: (token: string): Promise<ChatSessionSummary[]> =>
    apiRequest<ChatSessionSummary[]>("/v1/chats", { token }),

  get: (id: string, token: string): Promise<ChatSession> =>
    apiRequest<ChatSession>(`/v1/chats/${id}`, { token }),

  create: (
    body: { title?: string },
    token: string,
  ): Promise<ChatSessionSummary> =>
    apiRequest<ChatSessionSummary>("/v1/chats", {
      method: "POST",
      body,
      token,
    }),

  rename: (
    id: string,
    title: string,
    token: string,
  ): Promise<ChatSessionSummary> =>
    apiRequest<ChatSessionSummary>(`/v1/chats/${id}`, {
      method: "PATCH",
      body: { title },
      token,
    }),

  remove: (id: string, token: string): Promise<{ ok: boolean }> =>
    apiRequest<{ ok: boolean }>(`/v1/chats/${id}`, {
      method: "DELETE",
      token,
    }),

  // Append a message. The server auto-titles the session from the first
  // user message and bumps updatedAt — caller should re-fetch the sidebar
  // listing after the first user send to pick up the new title.
  appendMessage: (
    id: string,
    body: {
      role: "user" | "assistant";
      text?: string | null;
      images?: string[] | null;
      generationId?: string | null;
    },
    token: string,
  ): Promise<ChatMessageRow> =>
    apiRequest<ChatMessageRow>(`/v1/chats/${id}/messages`, {
      method: "POST",
      body,
      token,
    }),
};
